/**
 * Pure deep-link parser. Takes a raw URL string from the OS / another tab /
 * a `web+kaether:` handler dispatch, classifies it into one of the typed
 * `DeepLinkIntent` variants, or rejects it with a structured reason.
 *
 * The parser is SYNCHRONOUS and PURE: no `await`, no I/O, no globals beyond
 * the standard `URL` constructor and `TextEncoder`-free regex. The
 * signature-verifying decoders in `GroupInviteLink` and `CallLink` are NOT
 * invoked here — that's deferred to the confirmation surface so the parser
 * has no async surface to race with.
 *
 * THREAT MODEL (see `DeepLinkPolicy.ts` for the full write-up)
 *   1. Compile-time host allow-list. URL host must EXACTLY equal an entry in
 *      `ALLOWED_HOSTS` after lowercase. Cyrillic / IDN homographs surface in
 *      punycode form (`xn--…`) from the URL parser, so they cannot match.
 *   2. Length cap before any regex fires. Bounds parser cost on hostile
 *      inputs; also defangs regex amplification (the regexes here are linear-
 *      time anyway, but defence in depth is cheap).
 *   3. Per-route validators are character-set-pinned and length-pinned. The
 *      profile-handle regex mirrors the one in `e2ee/username/Username.ts`;
 *      invite / call-link bodies are base64url-only and length-bounded.
 *   4. Any path other than `/u/<handle>`, `/g/<invite>`, `/c/<call-link>` is
 *      rejected. No "if it's not one of these three, just open it in the
 *      browser" fallback — the router is closed, not open.
 *   5. Never auto-execute. The parser returns an INTENT; a separate confirma-
 *      tion surface is responsible for displaying the typed fields and
 *      gating the side effect behind a user gesture.
 *
 * Service-Worker importability guarantee:
 *   This file uses NO `window`, `document`, or DOM-only APIs. The standard
 *   `URL` constructor is available in SW contexts.
 */

import {
  ALLOWED_HOSTS,
  CALL_LINK_PATH_PREFIX,
  GROUP_INVITE_PATH_PREFIX,
  MAX_URL_LENGTH,
  PROFILE_PATH_PREFIX,
} from "./DeepLinkPolicy";
import type { DeepLinkIntent } from "./DeepLinkIntent";

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

/**
 * Closed discriminated union of every parse outcome. `kind: 'intent'` is the
 * single success branch; every other variant pinpoints WHY the URL was
 * rejected so logs / metrics can distinguish "looked like phishing" from
 * "user typo'd the URL" from "we shipped a bug."
 */
export type DeepLinkParseResult =
  | { readonly kind: "intent"; readonly intent: DeepLinkIntent }
  | { readonly kind: "rejectedTooLong"; readonly length: number }
  | { readonly kind: "rejectedMalformed"; readonly reason: string }
  | { readonly kind: "rejectedHost"; readonly host: string }
  | { readonly kind: "rejectedHandle"; readonly raw: string }
  | { readonly kind: "rejectedInvite"; readonly reason: string }
  | { readonly kind: "rejectedCallLink"; readonly reason: string }
  | { readonly kind: "rejectedPath"; readonly path: string };

// ---------------------------------------------------------------------------
// Per-route validators
// ---------------------------------------------------------------------------

/**
 * Handle path: `<canonicalName>.<discriminator>`.
 * Mirrors the username regex from `e2ee/username/Username.ts`:
 *   - canonicalName: 3–32 chars `[a-z0-9_]`, must NOT start with a digit
 *     (otherwise `name.NNNN` is ambiguous with a pure-numeric name like
 *     `1234.5678`).
 *   - discriminator: exactly 4 ASCII digits.
 *
 * Anchored to start-and-end so a trailing query/fragment cannot smuggle in
 * extra characters.
 */
const HANDLE_REGEX = /^([a-z_][a-z0-9_]{2,31})\.(\d{4})$/;

/**
 * Base64url charset for invite-link and call-link bodies. RFC 4648 §5
 * (without padding) — alphanumerics plus `-` and `_`. The codec bodies are
 * bounded length: invites are exactly 48 bytes → 64 base64url chars; call
 * links are ~180–200 bytes → ~240–270 base64url chars. We cap at 1024 to
 * leave headroom for a future schema bump without changing the parser.
 */
const BASE64URL_REGEX = /^[A-Za-z0-9_-]{16,1024}$/;

/**
 * Minimum body length below which we don't even bother. 48-byte payload →
 * 64 base64url chars; we accept slightly less to leave room for future
 * variants but reject obviously truncated inputs.
 */
const MIN_BODY_LENGTH = 16;

/** Custom-scheme prefixes — replicated from sibling modules to keep this
 *  file standalone (no cross-package imports beyond `DeepLinkPolicy`). */
const GROUP_INVITE_PREFIX = "kaether:group:invite:v1:";
const CALL_LINK_PREFIX = "kaether:call:link:v1:";

// ---------------------------------------------------------------------------
// parseDeepLink
// ---------------------------------------------------------------------------

/**
 * Parse a deep-link URL into a typed intent or a structured rejection.
 *
 * Accepts the canonical kaether universal-link forms:
 *   - `https://kaether.fr/u/<handle>`
 *   - `https://kaether.fr/g/<invite-body>`
 *   - `https://kaether.fr/c/<call-link-body>`
 *
 * The custom scheme (`web+kaether:`) is handled in `DeepLinkRegistration`
 * which unwraps it to the equivalent universal-link form before calling
 * back here. This parser sees ONLY https URLs whose host we control.
 */
export function parseDeepLink(url: string): DeepLinkParseResult {
  // 1. Length cap — first thing, before any URL parsing work.
  if (url.length > MAX_URL_LENGTH) {
    return { kind: "rejectedTooLong", length: url.length };
  }

  // 2. URL syntactic validity.
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { kind: "rejectedMalformed", reason: "URL constructor threw" };
  }

  // 3. Scheme must be https. We do not accept http on the off chance an
  //    attacker can MITM the redirect from http to https on the user's
  //    network — bind the brand to https-only at the parser level.
  if (parsed.protocol !== "https:") {
    return {
      kind: "rejectedMalformed",
      reason: `expected https, got ${parsed.protocol}`,
    };
  }

  // 4. Compile-time host allow-list. Browser-parsed `hostname` is already
  //    lowercase and punycode-encoded for IDN inputs; we lowercase again
  //    belt-and-braces.
  const host = parsed.hostname.toLowerCase();
  if (!ALLOWED_HOSTS.has(host)) {
    return { kind: "rejectedHost", host };
  }

  // 5. Per-path routing. Order matters only for readability — the prefixes
  //    are mutually exclusive.
  const path = parsed.pathname;

  if (path.startsWith(PROFILE_PATH_PREFIX)) {
    return parseProfilePath(path.slice(PROFILE_PATH_PREFIX.length));
  }
  if (path.startsWith(GROUP_INVITE_PATH_PREFIX)) {
    return parseGroupInvitePath(path.slice(GROUP_INVITE_PATH_PREFIX.length));
  }
  if (path.startsWith(CALL_LINK_PATH_PREFIX)) {
    return parseCallLinkPath(path.slice(CALL_LINK_PATH_PREFIX.length));
  }

  return { kind: "rejectedPath", path };
}

// ---------------------------------------------------------------------------
// Per-route helpers
// ---------------------------------------------------------------------------

function parseProfilePath(raw: string): DeepLinkParseResult {
  // The URL might be percent-encoded; decode first so the regex sees the
  // canonical form. A malformed `%` sequence throws — treat as malformed
  // handle.
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return { kind: "rejectedHandle", raw };
  }

  const match = HANDLE_REGEX.exec(decoded);
  if (!match) {
    return { kind: "rejectedHandle", raw: decoded };
  }
  const canonicalName = match[1];
  const discriminator = parseInt(match[2], 10);
  // Belt-and-braces: regex already enforces 0000–9999, but the conversion
  // could in theory go sideways on a future regex edit.
  if (
    !Number.isInteger(discriminator) ||
    discriminator < 0 ||
    discriminator > 9999
  ) {
    return { kind: "rejectedHandle", raw: decoded };
  }

  return {
    kind: "intent",
    intent: {
      kind: "openProfile",
      canonicalName,
      discriminator,
      // Reconstruct the handle ourselves — never echo the raw URL slice,
      // so even if percent-decoding produced something exotic we hand the
      // confirmation surface a clean string.
      handle: `${canonicalName}.${match[2]}`,
    },
  };
}

function parseGroupInvitePath(raw: string): DeepLinkParseResult {
  if (raw.length < MIN_BODY_LENGTH) {
    return { kind: "rejectedInvite", reason: `body too short: ${raw.length}` };
  }
  if (!BASE64URL_REGEX.test(raw)) {
    return {
      kind: "rejectedInvite",
      reason: "body has non-base64url characters",
    };
  }
  return {
    kind: "intent",
    intent: {
      kind: "joinGroup",
      inviteUri: GROUP_INVITE_PREFIX + raw,
      inviteBody: raw,
    },
  };
}

function parseCallLinkPath(raw: string): DeepLinkParseResult {
  if (raw.length < MIN_BODY_LENGTH) {
    return {
      kind: "rejectedCallLink",
      reason: `body too short: ${raw.length}`,
    };
  }
  if (!BASE64URL_REGEX.test(raw)) {
    return {
      kind: "rejectedCallLink",
      reason: "body has non-base64url characters",
    };
  }
  return {
    kind: "intent",
    intent: {
      kind: "joinCall",
      callLinkUri: CALL_LINK_PREFIX + raw,
      callLinkBody: raw,
    },
  };
}
